/*
 * Library page, Trash, cards, song groups, storage footer and the Library's dialogs (W2-10 projects-data).
 * Design tokens only (src/ui/styles/tokens.css); state is never shown by colour alone (icons and words go with it).
 * Layouts hold at 1280x600: the start cards wrap, the toolbar wraps under the title, list rows drop the stats column.
 */

/* ---------------------------------------------------------------- page */

.page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--color-bg);
  color: var(--color-text);
}

.header {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-2) var(--space-5);
  border-bottom: var(--border-width) solid var(--color-divider);
  background: var(--color-surface-1);
  min-height: var(--topbar-height);
}

.h1 {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  white-space: nowrap;
}

.h2 {
  margin: 0;
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
}

.search {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex: 0 1 420px;
  margin-left: auto;
  padding: 0 var(--space-2);
  height: var(--control-h-md);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
}

.search:focus-within {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.searchInput {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
}

.searchInput:focus {
  outline: none;
}

.headerActions {
  display: flex;
  gap: var(--space-1);
}

/* Without the search field (Trash) the actions stay at the right, as on the Library page (UX-089). */
.h1 + .headerActions {
  margin-left: auto;
}

/* "Empty Trash": a secondary button in the danger colour, not a solid red primary one (UX-089). */
.dangerOutline[data-variant] {
  border-color: var(--color-danger);
  color: var(--color-danger);
}

.main {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.muted {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  margin: 0;
}

.errorText,
.warningText {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--text-sm);
}

.errorText {
  color: var(--color-danger);
}

.warningText {
  color: var(--color-warning);
}

/* ---------------------------------------------------------------- start cards */

.start {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-3);
}

.startCard {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: var(--space-3);
  align-items: center;
  text-align: left;
  padding: var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface-1);
  color: var(--color-text);
  font: inherit;
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-standard);
}

/* The start card the next-step hint points at (UX-076). */
.startCard[data-highlight] {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 var(--focus-ring-width) var(--color-accent-muted);
}

.startCard > svg {
  grid-row: span 2;
  color: var(--color-accent);
}

.startCard:hover {
  background: var(--color-surface-hover);
}

.startCard:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.startCard[aria-disabled='true'] {
  color: var(--color-text-disabled);
  cursor: not-allowed;
}

.startTitle {
  font-weight: var(--weight-semibold);
}

.startBody {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.dropHint {
  margin: 0;
  text-align: center;
  color: var(--color-text-subtle);
  font-size: var(--text-sm);
}

/* ---------------------------------------------------------------- notices */

.notice {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-2);
}

.notice[data-tone='info'] {
  border-color: var(--color-info);
  background: var(--color-info-muted);
}

.notice > svg {
  flex: none;
  color: var(--color-info);
  margin-top: var(--space-1);
}

.noticeText {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.noticeText p {
  margin: 0;
  font-size: var(--text-sm);
}

.noticeTitle {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
}

.noticeActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

/* ---------------------------------------------------------------- charts toolbar */

.charts {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.count {
  margin-left: var(--space-2);
  color: var(--color-text-muted);
  font-weight: var(--weight-normal);
  font-size: var(--text-sm);
}

.toolbarControls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-4);
  margin-left: auto;
}

.loadError {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-danger);
  font-size: var(--text-sm);
}

.noMatch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
}

/* ---------------------------------------------------------------- groups and cards */

.grid {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

/* List rows stay readable instead of stretching across a wide window (UX-083). */
.grid[data-view='list'] {
  max-width: calc(1120px * var(--ui-scale));
}

.group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.group[data-view='list'] {
  display: grid;
  grid-template-columns: 64px 1fr;
  column-gap: var(--space-3);
  align-items: start;
}

.groupHeader {
  display: contents;
}

.group[data-view='grid'] .groupHeader {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
}

.groupThumb {
  grid-row: span 2;
  width: 64px;
  height: 36px;
}

.groupText {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
  min-width: 0;
}

.groupTitle {
  margin: 0;
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
}

.groupComposer {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.groupShared {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.cards {
  list-style: none;
  margin: 0;
  padding: 0;
  min-width: 0;
}

.cards[data-view='grid'] {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: var(--space-3);
}

.cards[data-view='list'] {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.card {
  position: relative;
  display: flex;
  min-width: 0;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-1);
}

.card[data-busy] {
  opacity: 0.7;
}

.card:hover {
  background: var(--color-surface-hover);
}

.cardMain {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.card[data-view='grid'] .cardMain {
  flex-direction: column;
  align-items: stretch;
  padding: var(--space-2);
}

.cardMain:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: calc(-1 * var(--focus-ring-width));
}

.cardMain[data-renaming] {
  cursor: default;
}

.cardText {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-4);
  padding-right: var(--space-6);
}

.card[data-view='grid'] .cardText {
  flex-direction: column;
  align-items: stretch;
  gap: var(--space-1);
  padding-right: 0;
}

.cardTitleRow {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  min-width: 0;
}

.cardTitle {
  font-weight: var(--weight-semibold);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cardSubtitle {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cardStats,
.cardEdited {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  white-space: nowrap;
}

.cardMenu {
  position: absolute;
  top: var(--space-1);
  right: var(--space-1);
}

.card[data-view='list'] .cardMenu {
  top: 50%;
  transform: translateY(-50%);
}

.flags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-3);
}

.flag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.flag[data-tone='warning'] {
  color: var(--color-warning);
}

.flag[data-tone='info'] {
  color: var(--color-info);
}

.flag[data-tone='ok'] {
  color: var(--color-success);
}

.thumb {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 36px;
  overflow: hidden;
  border-radius: var(--radius-sm);
  background: var(--color-surface-3);
  color: var(--color-text-subtle);
}

.card[data-view='grid'] .thumb {
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
}

.thumbImg {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.renameField {
  display: flex;
  flex: 1;
  min-width: 160px;
}

.renameInput,
.input {
  flex: 1;
  min-width: 0;
  height: var(--control-h-sm);
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font: inherit;
}

.renameInput:focus-visible,
.input:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.input[aria-invalid='true'] {
  border-color: var(--color-danger);
}

/* ---------------------------------------------------------------- trash */

.trash {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.trashHeader {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.trashHeader > :last-child:not(:first-child):not(h2) {
  margin-left: auto;
}

.trashList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.trashRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-1);
}

.trashText {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-3);
}

.trashMeta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-3);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.trashActions {
  display: flex;
  gap: var(--space-2);
}

/* ---------------------------------------------------------------- footer */

.footer {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-5);
  border-top: var(--border-width) solid var(--color-divider);
  background: var(--color-surface-1);
  font-size: var(--text-sm);
}

.footerRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-5);
}

.footerItem {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.footerItem[data-tone='ok'] > svg {
  color: var(--color-success);
}

.footerItem[data-tone='warning'] > svg {
  color: var(--color-warning);
}

.footerMeter {
  flex: 0 1 200px;
  min-width: 120px;
}

.footerLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
  color: var(--color-text-link);
}

.footerLink:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.footerNotes {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.footerNotes li {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.footerNotes li[data-tone='warning'] > svg {
  color: var(--color-warning);
}

.footerNotes li[data-tone='error'] {
  color: var(--color-danger);
}

.footerNotes li[data-tone='muted'] {
  color: var(--color-text-muted);
}

/* ---------------------------------------------------------------- dialogs */

.choiceList {
  list-style: none;
  margin: 0 0 var(--space-3);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  font-size: var(--text-sm);
}

.choiceList li {
  display: flex;
  gap: var(--space-2);
  align-items: flex-start;
}

.choiceList li > svg {
  flex: none;
  margin-top: 2px;
  color: var(--color-text-muted);
}

.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.fieldset {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  border: 0;
}

.legend {
  margin-bottom: var(--space-1);
  padding: 0;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
}

.radioCard {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
}

.radioCard[data-checked] {
  border-color: var(--color-accent-border);
  background: var(--color-accent-muted);
}

.radioCard > span {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.inputRow {
  display: grid;
  grid-template-columns: var(--label-width) 1fr;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--text-sm);
}

.preview {
  margin: 0;
  font-weight: var(--weight-semibold);
}

@media (max-width: 1100px) {
  .group[data-view='list'] {
    grid-template-columns: 1fr;
  }

  .groupThumb {
    display: none;
  }

  .cards[data-view='list'] .cardStats {
    display: none;
  }
}
